import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent, RefObject } from 'react';

const RETURN_MS = 500; // Slightly longer than the CSS return-to-rest transition.
const clamp = (value: number) => Math.max(-1, Math.min(1, value));

/** Positive Y rotation pushes the right edge away; positive X pushes the top away. */
export function cardboardTilt(x: number, y: number) {
  return { x: -clamp(y) * 26, y: clamp(x) * 26 };
}

/** Only entry/exit changes React state. Movement writes one transform per frame.
 *  The host is whatever the hand is over: the pin's and a section sticker's <button>, or a
 *  print sticker's <span> (PrintSticker), whose click belongs to the card around it. */
export default function useCardboardTilt<T extends HTMLElement = HTMLButtonElement>(buttonRef: RefObject<T>, enabled: boolean) {
  const surfaceRef = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(false);
  const hovering = useRef(false);
  const frame = useRef<number | null>(null);
  const release = useRef<number | null>(null);
  const point = useRef({ x: 0, y: 0 });

  const clearSurface = useCallback(() => {
    surfaceRef.current?.removeAttribute('data-tracking');
    surfaceRef.current?.style.removeProperty('--br-tilt-x');
    surfaceRef.current?.style.removeProperty('--br-tilt-y');
    buttonRef.current?.style.removeProperty('--br-shadow-x');
    buttonRef.current?.style.removeProperty('--br-shadow-y');
  }, [buttonRef]);

  const cancelPending = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    if (release.current !== null) window.clearTimeout(release.current);
    frame.current = release.current = null;
  }, []);

  const reset = useCallback(() => {
    cancelPending();
    hovering.current = false;
    clearSurface();
    setActive(false);
  }, [cancelPending, clearSurface]);

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);
  useEffect(() => {
    // A live preference change must stop motion, even while a pointer is still.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const pointer = window.matchMedia?.('(any-hover: hover) and (any-pointer: fine)');
    const onPreference = () => { if (reduced?.matches || !pointer?.matches) reset(); };
    const onVisibility = () => { if (document.hidden) reset(); };
    reduced?.addEventListener?.('change', onPreference);
    pointer?.addEventListener?.('change', onPreference);
    window.addEventListener('blur', reset);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelPending();
      reduced?.removeEventListener?.('change', onPreference);
      pointer?.removeEventListener?.('change', onPreference);
      window.removeEventListener('blur', reset);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [cancelPending, reset]);

  const allowed = (event: PointerEvent<T>) => enabled && event.pointerType === 'mouse'
    && window.matchMedia?.('(any-hover: hover) and (any-pointer: fine)').matches
    && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    && !buttonRef.current?.closest('[data-quiet=true]');

  const queuePoint = (event: PointerEvent<T>) => {
    point.current = { x: event.clientX, y: event.clientY };
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const bounds = buttonRef.current?.getBoundingClientRect();
      if (!bounds?.width || !bounds.height) return;
      // Measure the stationary hit target, never the tilted child (no feedback jitter).
      const tilt = cardboardTilt(
        (point.current.x - bounds.left) / bounds.width * 2 - 1,
        (point.current.y - bounds.top) / bounds.height * 2 - 1,
      );
      surfaceRef.current?.style.setProperty('--br-tilt-x', `${tilt.x.toFixed(2)}deg`);
      surfaceRef.current?.style.setProperty('--br-tilt-y', `${tilt.y.toFixed(2)}deg`);
      // A separate shadow stays on the paper while the printed face tips away.
      buttonRef.current?.style.setProperty('--br-shadow-x', `${(-tilt.y / 26 * 5).toFixed(2)}px`);
      buttonRef.current?.style.setProperty('--br-shadow-y', `${(tilt.x / 26 * 4).toFixed(2)}px`);
    });
  };

  const onPointerEnter = (event: PointerEvent<T>) => {
    if (!allowed(event)) return;
    cancelPending();
    hovering.current = true;
    setActive(true);
    surfaceRef.current?.setAttribute('data-tracking', 'true');
    queuePoint(event);
  };
  const onPointerMove = (event: PointerEvent<T>) => {
    if (!hovering.current) return;
    if (!allowed(event)) { reset(); return; }
    queuePoint(event);
  };
  const onPointerLeave = () => {
    if (!hovering.current) return;
    cancelPending();
    hovering.current = false;
    clearSurface();
    // Keep the edge geometry until the return transition finishes.
    release.current = window.setTimeout(() => { release.current = null; setActive(false); }, RETURN_MS);
  };

  return { surfaceRef, active, handlers: { onPointerEnter, onPointerMove, onPointerLeave, onPointerCancel: reset } };
}
